import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import { buildColorMapForNames, resolveColorForName } from '../../utils/nameColors';
import { transformHistoryDataForChart, buildDashboardDateParams } from '../../utils/dashboardChartData';
import PerformerChart from './PerformerChart';
import LinkStatsChart from './LinkStatsChart';

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const DashboardSummaryTab = ({
  performers,
  dateRange,
  fromDate = '',
  selectedRangeLabel,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!performers.length) {
        setLoading(false);
        return;
      }

      if ((dateRange === 'custom' || dateRange === 'single') && !fromDate) {
        setLoading(false);
        setSummary(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/applications/dashboard-summary`,
          {
            params: {
              userIds: performers.map((performer) => performer._id).join(','),
              ...buildDashboardDateParams({ days: dateRange, fromDate }),
              excludeAutomatedEmails: 'true',
            },
          }
        );
        setSummary(response.data);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load summary');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [performers, dateRange, fromDate]);

  const dateFilter = useMemo(
    () => ({
      days: dateRange,
      fromDate: (dateRange === 'custom' || dateRange === 'single') ? fromDate : '',
    }),
    [dateRange, fromDate]
  );

  const colorMap = useMemo(() => {
    const names = [
      ...(summary?.resumeBids || []).map((item) => item.profile),
      ...(summary?.resumeCalls || []).map((item) => item.profile),
      ...(summary?.resumeMetrics || []).map((item) => item.resumeName),
      ...(summary?.aggregatedBidChart?.profiles || []),
      ...(summary?.aggregatedCallChart?.profiles || []),
    ];
    const summaryColors = buildColorMapForNames(names, isDarkMode);
    return summaryColors;
  }, [summary, isDarkMode]);

  const aggregatedBidChartData = useMemo(() => {
    if (!summary?.aggregatedBidChart) return { labels: [], datasets: [] };
    return transformHistoryDataForChart(summary.aggregatedBidChart, dateFilter, colorMap, isDarkMode);
  }, [summary?.aggregatedBidChart, dateFilter, colorMap, isDarkMode]);

  const aggregatedCallChartData = useMemo(() => {
    if (!summary?.aggregatedCallChart) return { labels: [], datasets: [] };
    return transformHistoryDataForChart(summary.aggregatedCallChart, dateFilter, colorMap, isDarkMode);
  }, [summary?.aggregatedCallChart, dateFilter, colorMap, isDarkMode]);

  const tableCellSx = {
    color: (theme) => theme.palette.text.primary,
    fontWeight: isDarkMode ? 500 : 400,
  };

  const tableHeadCellSx = {
    fontWeight: 700,
    color: (theme) => theme.palette.text.primary,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  if (!summary) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No summary data available.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <LinkStatsChart
        dateRange={dateRange}
        fromDate={fromDate}
        rangeLabel={selectedRangeLabel}
      />

      <PerformerChart
        performer={{ label: 'General Bid Performance' }}
        chartData={aggregatedBidChartData}
        rangeLabel={selectedRangeLabel}
        subtitle="Bid History by Resume (Stacked)"
        valueLabel="Number of Bids"
      />

      <PerformerChart
        performer={{ label: 'General Call Performance' }}
        chartData={aggregatedCallChartData}
        rangeLabel={selectedRangeLabel}
        subtitle="Interview Invitations by Resume (Stacked)"
        valueLabel="Number of Calls"
      />

      <Paper sx={{ p: 2, color: 'text.primary' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
          Resume Metrics
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Calls count Interview Invitation emails only. Names use the resume&apos;s original name.
        </Typography>
        <TableContainer sx={{ color: 'text.primary' }}>
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': {
                color: 'inherit',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Resume</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>Bids</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>Calls</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>Bid %</TableCell>
                <TableCell sx={tableHeadCellSx}>Performance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(summary.resumeMetrics || [])
                .filter((row) => row.resumeName !== 'No profile')
                .map((row) => {
                const bidPercent = summary.totalBids
                  ? (row.bids / summary.totalBids) * 100
                  : 0;
                const callPercent = summary.totalCalls
                  ? (row.calls / summary.totalCalls) * 100
                  : 0;
                const callsDisplay = summary.totalCalls
                  ? `${row.calls}(${formatPercent(callPercent)})`
                  : String(row.calls);

                return (
                  <TableRow key={row.resumeName} hover>
                    <TableCell sx={tableCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: resolveColorForName(row.resumeName, colorMap, isDarkMode),
                            flexShrink: 0,
                          }}
                        />
                        {row.resumeName}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={tableCellSx}>{row.bids}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{callsDisplay}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatPercent(bidPercent)}</TableCell>
                    <TableCell sx={tableCellSx}>{row.performanceLabel}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2, color: 'text.primary' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
          URL Patterns by Bid Count
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Resume-agnostic breakdown of where bids were submitted in this period.
        </Typography>

        {!summary.urlPatterns?.length ? (
          <Typography color="text.secondary">No URL data in this period.</Typography>
        ) : (
          <Box sx={{ width: '100%', height: Math.max(240, summary.urlPatterns.length * 36) }}>
            <ResponsiveContainer>
              <BarChart
                data={summary.urlPatterns}
                layout="vertical"
                margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis type="number" allowDecimals={false} tick={{ fill: theme.palette.text.primary }} />
                <YAxis
                  type="category"
                  dataKey="pattern"
                  width={140}
                  tick={{ fill: theme.palette.text.primary, fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value, _name, props) => [
                    `${value} bids (${formatPercent(props.payload.percent)})`,
                    'Count',
                  ]}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                  }}
                />
                <Bar dataKey="count" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardSummaryTab;
